import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Calendar, MapPin, Users, ArrowRight, Loader2, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SignupSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('failed');
      setError('No payment session found');
      return;
    }

    // Poll for payment status
    const checkPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('failed');
        setError('Payment verification timed out. Please check your email for confirmation.');
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/payments/status/${sessionId}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || 'Failed to verify payment');
        }

        if (data.payment_status === 'paid') {
          setPaymentData(data);
          setStatus('success');
          return;
        } else if (data.payment_status === 'cancelled' || data.payment_status === 'expired') {
          setStatus('failed');
          setError('Payment was cancelled or expired. Please try again.');
          return;
        }

        // Continue polling if still pending
        setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
      } catch (err) {
        console.error('Payment status check error:', err);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus('failed');
          setError(err.message);
        }
      }
    };

    checkPaymentStatus();
  }, [searchParams]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <div className="max-w-lg mx-auto text-center px-6">
          <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-6" />
          <h1 className="font-playfair text-3xl font-bold mb-4">Verifying Payment...</h1>
          <p className="text-muted-foreground">
            Please wait while we confirm your payment. This should only take a moment.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
        <div className="max-w-lg mx-auto text-center px-6">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="font-playfair text-3xl font-bold mb-4">Payment Issue</h1>
          <p className="text-muted-foreground mb-8">
            {error || 'There was an issue with your payment. Please try again or contact support.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1"
            >
              Try Again
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 glass-card rounded-full font-semibold transition-all hover:-translate-y-1"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  const ticketNames = {
    standard_pass: 'Standard Pass',
    vip_pass: 'VIP Pass',
    virtual_pass: 'Virtual Pass',
    sponsorship: 'Partner-/Sponsorship'
  };

  return (
    <div className="min-h-screen pt-24 pb-12 flex items-center justify-center">
      <div className="max-w-lg mx-auto text-center px-6">
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-green-500" />
        </div>
        <h1 className="font-playfair text-3xl font-bold mb-4">Payment Successful!</h1>
        <p className="text-muted-foreground mb-8">
          Thank you for registering for the Quantum Housing Conference. 
          You will receive a confirmation email shortly with your ticket details.
        </p>
        
        {paymentData?.registration_id && (
          <p className="text-sm text-muted-foreground mb-4">
            Registration ID: <span className="font-mono text-primary">{paymentData.registration_id}</span>
          </p>
        )}

        <div className="glass-card rounded-xl p-6 mb-8 text-left">
          <h3 className="font-semibold mb-4">Your Registration</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-primary" />
              <span>April 17th, 2026 • 12:00 PM EST</span>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-primary" />
              <span>Tsai Auditorium, Harvard University</span>
            </div>
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-primary" />
              <span>{ticketNames[paymentData?.ticket_type] || paymentData?.ticket_type}</span>
            </div>
            {paymentData?.amount && (
              <div className="pt-3 mt-3 border-t border-border">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount Paid:</span>
                  <span className="font-semibold text-primary">${paymentData.amount.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <Link
          to="/"
          className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1"
        >
          Back to Home
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </div>
  );
};

export default SignupSuccessPage;
